import { LitElement, html, css } from 'lit'
import { repeat } from 'lit/directives/repeat.js'
import { api } from '../api.js'
import { navigate } from '../router.js'
import { toast } from './app-toast.js'
import './entry-row.js'

const STATE_LABEL = {
  current: 'Today', needs_audit: 'Needs Audit', past_unreviewed: 'Needs review',
  review_complete: 'Reviewed', future: 'Future', approved: 'Approved'
}
// The day badge is a pill in the state's own color, matching the calendar cells.
const STATE_BADGE = {
  current: 'background:var(--sky-500);color:var(--white)',
  needs_audit: 'background:var(--chartreuse-200);color:var(--chartreuse-800)',
  past_unreviewed: 'background:var(--chartreuse-500);color:var(--eggplant-500)',
  review_complete: 'background:var(--eggplant-600);color:var(--white)',
  future: 'background:var(--white);color:var(--neutral-600);box-shadow:inset 0 0 0 1px var(--neutral-200)',
  approved: 'background:var(--neutral-200);color:var(--neutral-800)'
}

// Day review, restyled to the BrandExtract design system.
class PageDay extends LitElement {
  static properties = {
    params: { type: Array },
    _day: { state: true },
    _loading: { state: true },
    _auditing: { state: true },
    // The entries ticked for a merge. Held here rather than on the rows so the
    // toolbar can count them and show the Merge button once there are two.
    _selected: { state: true },
    // The mode currently being merged ('combine' | 'overlap'), so the spinner lands
    // on the button that was clicked; null when no merge is in flight.
    _mergeBusy: { state: true }
  }

  static styles = css`
    *, *::before, *::after { box-sizing: border-box; }
    /* A block host gives the docked footer below a definite sticky containing block. */
    :host { display: block; }

    a.back { display: inline-block; font-size: 14px; color: var(--neutral-600);
             margin-bottom: 14px; cursor: pointer; text-decoration: none; }
    a.back:hover { color: var(--eggplant-500); }

    .head { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; }
    .title { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
    h1 { margin: 0; font-family: var(--font-display); font-size: 34px; line-height: 1.1;
         font-weight: 700; color: var(--eggplant-500); }
    .badge { font-family: var(--font-eyebrow); font-size: 10px; font-weight: 700;
             letter-spacing: 0.12em; text-transform: uppercase; border-radius: 999px;
             padding: 5px 12px; position: relative; top: 2px; white-space: nowrap; }

    .daynav { display: flex; gap: 8px; flex-shrink: 0; }
    .daynav button { width: 34px; height: 34px; border-radius: 6px; cursor: pointer;
                     font: inherit; font-size: 15px; line-height: 1; color: var(--eggplant-500);
                     background: var(--white); border: 1px solid var(--neutral-100); }
    .daynav button:hover { border-color: var(--eggplant-300); }

    .summary { font-size: 14.5px; line-height: 1.55; color: var(--neutral-600);
               margin: 12px 0 24px; max-width: 760px; }
    .summary strong { color: var(--eggplant-500); }

    .entries { display: block; }
    .empty { padding: 22px; background: var(--white); border: 1px solid var(--neutral-100);
             border-radius: 10px; box-shadow: var(--shadow-cell); color: var(--neutral-600);
             font-size: 14.5px; }

    /* The day's actions are docked to the bottom of the viewport so they stay
       reachable however long the entry list runs. The band bleeds the full width of
       the viewport on its own white surface — hairline plus an upward shadow — so it
       reads as a fixed bar the entries scroll underneath, and it ends flush with the
       bottom of the page once fully scrolled. The buttons inside stay on the content
       column. */
    .toolbar { position: sticky; bottom: 0; z-index: 10;
               width: 100vw;
               margin: 28px calc(50% - 50vw) calc(-1 * var(--page-pad-bottom));
               background: var(--white); border-top: 1px solid var(--neutral-100);
               box-shadow: 0 -4px 18px rgba(51, 35, 75, .10); }
    .toolbar .inner { display: flex; align-items: center; justify-content: space-between;
                      gap: 16px; flex-wrap: wrap; max-width: var(--page-max);
                      margin: 0 auto; padding: 16px var(--page-gutter); }
    .toolbar .left { display: flex; gap: 12px; flex-wrap: wrap; }

    /* Design-system buttons: squared off, no radius. Outline for secondary
       actions, chartreuse CTA for the one primary action on the page. */
    button.btn { font-family: var(--font-sans); font-size: 14px; font-weight: 600;
                 line-height: 20px; padding: 8px 16px; cursor: pointer; border: none;
                 border-radius: 0; background: transparent; color: var(--eggplant-500);
                 box-shadow: inset 0 0 0 1px var(--eggplant-500); white-space: nowrap;
                 transition: background-color .18s ease, color .18s ease; }
    button.btn:hover:not(:disabled) { background: var(--eggplant-500); color: var(--neutral-50); }
    button.btn.cta { box-shadow: none; background: var(--chartreuse-500); color: var(--eggplant-500);
                     font-size: 16px; line-height: 24px; padding: 12px 24px; }
    button.btn.cta:hover:not(:disabled) { background: var(--chartreuse-600); color: var(--eggplant-500); }
    button.btn:disabled { opacity: 0.4; cursor: not-allowed; }
    /* Merge: filled, so it reads as the live action once rows are ticked, without
       taking the chartreuse reserved for the day's one CTA. */
    button.btn.solid { background: var(--eggplant-600); color: var(--white);
                       box-shadow: inset 0 0 0 1px var(--eggplant-600); }
    button.btn.solid:hover:not(:disabled) { background: var(--eggplant-700);
                                            box-shadow: inset 0 0 0 1px var(--eggplant-700); }

    /* Audit progress banner + inline spinners. */
    @keyframes zg-spin { to { transform: rotate(360deg); } }
    .spinner { display: inline-block; width: 0.9em; height: 0.9em; border: 2px solid currentColor;
               border-right-color: transparent; border-radius: 50%; animation: zg-spin 0.6s linear infinite;
               vertical-align: -0.15em; }
    .audit-status { display: flex; align-items: center; gap: 10px; margin: 16px 0;
                    padding: 12px 16px; border-radius: 10px; background: var(--chartreuse-bg);
                    box-shadow: inset 0 0 0 1px var(--chartreuse-300);
                    color: var(--chartreuse-800); font-size: 14px; }

    @media (max-width: 640px) {
      h1 { font-size: 24px; }
      /* Docked, the bar has to stay short: the secondary actions share a wrapping
         row and only the CTA spans the width. */
      .toolbar .inner { flex-direction: column; align-items: stretch; gap: 10px;
                        padding: 12px var(--page-gutter); }
      .toolbar .left > button.btn { flex: 1 1 auto; }
      .toolbar .inner > button.btn { width: 100%; }
    }
  `

  get date() { return this.params && this.params[0] }

  connectedCallback() {
    super.connectedCallback()
    this._selected = []
    this._load()
    this.addEventListener('entry-action', this._onAction)
    this.addEventListener('entry-select', this._onSelect)
  }

  updated(changed) {
    if (changed.has('params')) { this._stopPolling(); this._selected = []; this._load() }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.()
    this._stopPolling()
  }

  // Return to the calendar on the month this day belongs to, so navigating
  // month → day → back keeps the user on the month they were viewing.
  _backToCalendar() {
    navigate(`/calendar/${(this.date || '').slice(0, 7)}`)
  }

  // Jump to the previous/next calendar day. Computed in UTC so it never drifts.
  _shiftDay(delta) {
    const d = new Date(`${this.date}T00:00:00Z`)
    d.setUTCDate(d.getUTCDate() + delta)
    navigate(`/days/${d.toISOString().slice(0, 10)}`)
  }

  async _load() {
    this._loading = true
    try {
      this._day = await api.get(`/days/${this.date}`)
      // A merge, an unmerge or an audit changes which rows exist; drop anything
      // selected that is no longer there (or no longer mergeable) rather than
      // sending a stale id back to the server.
      const stillThere = new Set(this._mergeable.map(e => e.id))
      this._selected = this._selected.filter(id => stillThere.has(id))
      // The server runs a needs-audit day's audit in the background and returns
      // auditing:true; show the banner and poll until it finishes.
      if (this._day?.auditing) { this._auditing = true; this._startPolling() }
      else { this._auditing = false; this._stopPolling() }
    } catch (err) {
      if (err.code === 'DAY_APPROVED') {
        toast('This day has been approved in Workamajig and cannot be edited', 'error')
        this._backToCalendar()
        return
      }
      toast(err.message, 'error')
    } finally {
      this._loading = false
    }
  }

  // While a background audit runs, poll its status; when it ends, reload the day
  // so the discovered entries appear.
  _startPolling() {
    if (this._pollTimer) return
    this._pollTimer = setInterval(async () => {
      let s
      try { s = await api.get(`/days/${this.date}/audit/status`) }
      catch { return } // transient error — keep polling
      if (s.running) return
      this._stopPolling()
      toast(s.error ? `Audit failed: ${s.error}` : 'Audit complete', s.error ? 'error' : 'success')
      await this._load()
    }, 2500)
  }

  _stopPolling() {
    if (this._pollTimer) { clearInterval(this._pollTimer); this._pollTimer = null }
  }

  _auditBanner() {
    return html`
      <div class="audit-status" role="status" aria-live="polite">
        <span class="spinner"></span>
        Running audit — analyzing your activity across your connected tools. This can take up to two minutes.
      </div>`
  }

  async _onAction(e) {
    const { action, id } = e.detail
    try {
      if (action === 'approve') await api.post(`/entries/${id}/approve`)
      else if (action === 'reject') await api.post(`/entries/${id}/reject`)
      else if (action === 'delete') await api.delete(`/entries/${id}`)
      else if (action === 'unmerge') {
        const result = await api.post(`/entries/${id}/unmerge`)
        const n = result?.restored?.length || 0
        toast(`Unmerged — ${n} ${n === 1 ? 'entry is' : 'entries are'} back`, 'success')
      }
    } catch (err) {
      toast(err.message, 'error')
    } finally {
      // Always reload — on success to show the new state, on failure to clear
      // the row's in-progress spinner and restore the editable entry.
      await this._load()
    }
  }

  // --- Merging two or more of the day's drafts by hand ----------------------

  // Every row the user is allowed to merge: Ziggy's own un-reviewed candidates.
  // Mirrors the server's rule (services/entries.service.js, assertMergeable).
  get _mergeable() {
    return (this._day?.entries || []).filter(e => e.source === 'ziggy' && e.status === 'draft')
  }

  _onSelect(e) {
    const { id, selected } = e.detail
    this._selected = selected
      ? [...this._selected, id].filter((v, i, a) => a.indexOf(v) === i)
      : this._selected.filter(v => v !== id)
  }

  // Merge the ticked rows into one, then reload the day so the survivor and its
  // "Merged" band replace them.
  //
  // `mode` is the hours rule, and it's the only thing that separates the two
  // buttons: 'combine' adds the ticked rows up (separate work that belongs on one
  // line), 'overlap' keeps the largest (the same minutes recorded in two places).
  // Either way the server joins the constituents' descriptions — see
  // mergedDescription in services/entry-merge.service.js.
  async _merge(mode) {
    const entryIds = [...this._selected]
    if (entryIds.length < 2) return
    this._mergeBusy = mode
    try {
      await api.post('/entries/merge', { entryIds, mode })
      toast(`Merged ${entryIds.length} entries into one`, 'success')
      this._selected = []
    } catch (err) {
      toast(err.message, 'error')
    } finally {
      this._mergeBusy = null
      await this._load()
    }
  }

  // The two ways to fold the ticked rows together, shown side by side once there
  // are at least two picks. Both carry the count, so the button says how many rows
  // it is about to take; the titles carry the difference, since which one you want
  // depends on whether those rows are different work or the same work twice.
  _mergeButtons() {
    const n = this._selected.length
    const busy = !!this._mergeBusy || this._auditing
    const button = (mode, label, title) => html`
      <button class="btn ${mode === 'combine' ? 'solid' : ''}" ?disabled=${busy}
              title=${title} @click=${() => this._merge(mode)}>
        ${this._mergeBusy === mode ? html`<span class="spinner"></span> Merging…` : `${label} ${n}`}
      </button>`
    return html`
      ${button('combine', 'Combine',
        `Separate work on one line: add the ${n} entries' hours up and join their descriptions`)}
      ${button('overlap', 'Smush',
        `The same work recorded ${n} times: keep the largest hours and join the descriptions`)}`
  }

  async _addRow() {
    try {
      await api.post(`/days/${this.date}/entries`)
      await this._load()
    } catch (err) {
      toast(err.message, 'error')
    }
  }

  // Mark the day reviewed (or reopen it) without submitting to Workamajig — a
  // local "I've looked at this" flag that turns the calendar day done.
  async _setReviewed(reviewed) {
    if (this._auditing) return
    try {
      await api.post(`/days/${this.date}/review`, { reviewed })
      toast(reviewed ? 'Day marked as reviewed' : 'Day reopened for editing', 'success')
      await this._load()
    } catch (err) {
      toast(err.message, 'error')
    }
  }

  async _reaudit() {
    if (this._auditing) return
    this._auditing = true
    try {
      await api.post(`/days/${this.date}/audit`) // returns immediately (runs in background)
      this._startPolling()
    } catch (err) {
      toast(err.message, 'error')
      this._auditing = false
    }
  }

  render() {
    // Before the day loads: if an audit is running (first open of a needs-audit
    // day), show the audit banner rather than a bare "Loading…".
    if (!this._day) return this._auditing ? this._auditBanner() : html`<p>Loading…</p>`
    const d = this._day
    const dateLabel = new Date(`${d.date}T00:00:00Z`)
      .toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
    // A reviewed day is settled — no re-audit until it's reopened (which clears
    // review_complete back to unreviewed).
    const canAudit = d.state === 'current' || d.state === 'needs_audit' ||
      d.state === 'past_unreviewed'
    const auditLabel = d.auditedAt ? 'Re-run audit' : 'Run audit'
    // Reviewing is a local flag; it makes no sense for future days, and approved
    // days are locked (loadDay 423s before we ever render one).
    const isReviewed = d.state === 'review_complete'
    const canReview = d.state !== 'future' && d.state !== 'approved'
    // A day can't be marked reviewed until it's been audited, nor while Ziggy's
    // draft suggestions are still unresolved. Reopening is always allowed.
    const pendingDrafts = d.entries.filter(e => e.source === 'ziggy' && e.status === 'draft').length
    let reviewBlockReason = ''
    if (!isReviewed) {
      if (!d.auditedAt) reviewBlockReason = 'Run an audit before marking the day reviewed'
      else if (pendingDrafts > 0) {
        reviewBlockReason = `Approve or dismiss ${pendingDrafts} pending draft ${pendingDrafts === 1 ? 'entry' : 'entries'} first`
      }
    }
    const blockReview = !!reviewBlockReason

    return html`
      <a class="back" @click=${this._backToCalendar}>‹ Calendar</a>
      <div class="head">
        <div class="title">
          <h1>${dateLabel}</h1>
          <span class="badge" style=${STATE_BADGE[d.state] || STATE_BADGE.future}>
            ${STATE_LABEL[d.state] || d.state}
          </span>
        </div>
        <div class="daynav">
          <button title="Previous day" aria-label="Previous day" @click=${() => this._shiftDay(-1)}>‹</button>
          <button title="Next day" aria-label="Next day" @click=${() => this._shiftDay(1)}>›</button>
        </div>
      </div>
      <p class="summary">
        Ziggy found <strong>${d.discoveredHours.total}h</strong>
        (${d.discoveredHours.billable}h billable, ${d.discoveredHours.nonbillable}h non-billable).
        Review each suggestion, fill any gaps, then approve to save to Workamajig.
      </p>

      ${this._auditing ? this._auditBanner() : ''}

      <div class="entries">
        ${d.entries.length === 0
          ? html`<p class="empty">No time entries for this day yet.</p>`
          : repeat(d.entries, entry => entry.id, entry => html`
            <entry-row .entry=${entry}
                       .selected=${this._selected.includes(entry.id)}></entry-row>`)}
      </div>

      <div class="toolbar">
        <div class="inner">
          <div class="left">
            ${canAudit ? html`
              <button class="btn" ?disabled=${this._auditing} @click=${this._reaudit}>
                ${this._auditing ? html`<span class="spinner"></span> Auditing…` : auditLabel}
              </button>` : ''}
            <button class="btn" ?disabled=${this._auditing} @click=${this._addRow}>+ Add row</button>
            ${this._selected.length > 1 ? this._mergeButtons() : ''}
          </div>
          ${canReview ? html`
            <button class="btn ${isReviewed ? '' : 'cta'}"
                    ?disabled=${this._auditing || blockReview}
                    title=${reviewBlockReason}
                    @click=${() => this._setReviewed(!isReviewed)}>
              ${isReviewed ? 'Reopen for editing' : 'Mark as reviewed'}
            </button>` : ''}
        </div>
      </div>
    `
  }
}

customElements.define('page-day', PageDay)
